/**
 * Ghost — the add tile at the seam (BRIEF §4) or, at rest, at position 0 of the whole wall (BRIEF
 * Iteration II §2/7: "the ghost stands at rest too, first" — reverses the earlier "no words, no
 * ghost"). Dashed, tapped to arm the column on the house baseline, with the query's words as the
 * direction when there is a query (see below).
 *
 * **At rest** (board `FirstGlance`, validated 2026-09-18) the cell carries two sentences, two
 * places, two tones: `atRest.rule` — the wall's one stated rule ("Pick any picture and change it."
 * at Toile) — at the TOP, in the primary text colour; the pen and `atRest.own` — this cell's own
 * act ("Or start from nothing.") — grouped at the BOTTOM, in tungsten. `rule: null` (a house with
 * no rule of its own) renders exactly what a single-sentence ghost always did — pen +
 * `own`, centred, no split — so the pair costs nothing where a house never asked for it.
 *
 * **Under a query** (`words` non-empty, following the search field live) the cell shows its verb
 * made visible: `GHOST_VERB_LABEL` ("Make a picture from") above the query's words — the pills'
 * labels and the typed words, comma-joined (`searchModel.makeDirection`, "linocut fox, autumn") — the
 * exact sentence the accessible name already carried: one string feeds both, so the visible line and
 * `aria-label` can never read differently. `onTap` arms on exactly those words.
 */
import type {GhostAtRest} from './theme'
import styles from './Ghost.module.scss'

/** The verb line a query puts above the typed words — one definition, read by both the visible
 *  span and the accessible name below, so neither can drift from the other. */
export const GHOST_VERB_LABEL = 'Make a picture from'

export function Pen() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3 a2.83 2.83 0 0 1 4 4 L7.5 20.5 L2 22 L3.5 16.5 Z" />
        </svg>
    )
}

export function Ghost({words, atRest, onTap}: {
    words: string
    atRest: GhostAtRest
    onTap: () => void
}) {
    const hasQuery = words.trim().length > 0
    if (hasQuery) {
        return (
            <button
                type="button"
                className={styles.ghost}
                style={{aspectRatio: '2 / 3'}}
                onClick={onTap}
                aria-label={`${GHOST_VERB_LABEL}: ${words}`}
            >
                <span className={styles.pen} aria-hidden="true"><Pen /></span>
                <span className={styles.verb}>{GHOST_VERB_LABEL}</span>
                <span className={styles.words}>{words}</span>
            </button>
        )
    }

    // At rest, no rule: exactly the single-sentence cell this was before the pair existed — pen +
    // the house's own act, centred, nothing split top/bottom.
    if (!atRest.rule) {
        return (
            <button
                type="button"
                className={styles.ghost}
                style={{aspectRatio: '2 / 3'}}
                onClick={onTap}
                aria-label={`${GHOST_VERB_LABEL}: ${atRest.own}`}
            >
                <span className={styles.pen} aria-hidden="true"><Pen /></span>
                <span className={styles.words}>{atRest.own}</span>
            </button>
        )
    }

    return (
        <button
            type="button"
            className={`${styles.ghost} ${styles.split}`}
            style={{aspectRatio: '2 / 3'}}
            onClick={onTap}
            aria-label={`${atRest.rule} ${atRest.own}`}
        >
            <span className={styles.rule}>{atRest.rule}</span>
            <span className={styles.ownRow}>
                <span className={styles.pen} aria-hidden="true"><Pen /></span>
                <span className={styles.words}>{atRest.own}</span>
            </span>
        </button>
    )
}
